import { ref, onMounted, onUnmounted, computed } from 'vue'
import { get } from '../../foundation/js/api.js'
import { formatAmount } from '../../foundation/js/utils/numberUtils.js'

const POLL_DELAY_MS = 1000
const MAX_PREVIOUS = 4

export function useStreamDonations(props) {
    const campaign = ref(null)
    const liveTotalAmount = ref(0)
    const pendingQueue = ref([])
    const currentNew = ref(null)
    const previousDonations = ref([])
    const seenIds = new Set()
    let primed = false
    let donationIdCounter = 0
    let currentNewShownAt = 0
    let campaignTimer = null
    let donationTimer = null
    let destroyed = false

    const name = computed(() => campaign.value?.details?.name ?? '')
    const totalAmount = computed(() =>
        formatAmount(
            liveTotalAmount.value || campaign.value?.details?.totalAmount || 0,
            0
        )
    )
    const targetAmount = computed(() =>
        formatAmount(campaign.value?.details?.targetAmount ?? 0, 0)
    )
    const progressPercent = computed(() => {
        const total =
            liveTotalAmount.value || campaign.value?.details?.totalAmount || 0
        const target = campaign.value?.details?.targetAmount ?? 0
        if (target <= 0) return 1
        return Math.min((total / target) * 100, 100).toFixed(2)
    })

    function mapDonation(d) {
        return {
            id: ++donationIdCounter,
            amount: d.amount,
            donorName:
                [d.firstName, d.lastName].filter(Boolean).join(' ') || 'Anonym'
        }
    }

    function promoteCurrentToHistory() {
        if (currentNew.value) {
            previousDonations.value = [
                { ...currentNew.value, shownAt: Date.now() },
                ...previousDonations.value
            ].slice(0, MAX_PREVIOUS)
            currentNew.value = null
        }
    }

    function prunePrevious() {
        const ttl = props.maxNyDonationSek * 1000
        const now = Date.now()
        const next = previousDonations.value.filter(d => now - d.shownAt < ttl)
        if (next.length !== previousDonations.value.length) {
            previousDonations.value = next
        }
    }

    function promoteNext() {
        promoteCurrentToHistory()
        const next = pendingQueue.value.shift()
        if (next) {
            currentNew.value = next
            currentNewShownAt = Date.now()
        }
    }

    function checkPromotion() {
        const hasWaiting = pendingQueue.value.length > 0

        if (!currentNew.value) {
            if (hasWaiting) promoteNext()
            return
        }

        const timeShown = Date.now() - currentNewShownAt
        if (timeShown >= props.maxNyDonationSek * 1000) {
            currentNew.value = null
        } else if (hasWaiting && timeShown >= props.minNyDonationSek * 1000) {
            promoteNext()
        }
    }

    async function fetchCampaign() {
        if (!props.fundraisingId) return
        const url = `${document.location.origin}/da/api/personalFundraising/${props.fundraisingId}`
        const result = await get(url)
        if (result) campaign.value = result
    }

    async function fetchDonations() {
        if (!props.fundraisingId) return
        const url = `${document.location.origin}/da/api/personalFundraising/${props.fundraisingId}/donations`
        const result = await get(url)
        if (!Array.isArray(result)) return

        liveTotalAmount.value = result.reduce(
            (sum, d) => sum + (d.amount ?? 0),
            0
        )

        if (!primed) {
            result.forEach(d => seenIds.add(d.donationId))
            primed = true
            return
        }

        const fresh = []
        for (let i = result.length - 1; i >= 0; i--) {
            const d = result[i]
            if (!seenIds.has(d.donationId)) {
                seenIds.add(d.donationId)
                fresh.push(mapDonation(d))
            }
        }
        if (fresh.length) {
            pendingQueue.value = [...pendingQueue.value, ...fresh]
        }
    }

    async function loopCampaign() {
        await fetchCampaign()
        if (!destroyed) campaignTimer = setTimeout(loopCampaign, POLL_DELAY_MS)
    }

    async function loopDonations() {
        await fetchDonations()
        checkPromotion()
        prunePrevious()
        if (!destroyed) donationTimer = setTimeout(loopDonations, POLL_DELAY_MS)
    }

    function handleFakeDonation(e) {
        const d = e.detail || {}
        pendingQueue.value = [...pendingQueue.value, mapDonation(d)]
        checkPromotion()
        prunePrevious()
    }

    onMounted(async () => {
        await fetchCampaign()
        await fetchDonations()
        campaignTimer = setTimeout(loopCampaign, POLL_DELAY_MS)
        donationTimer = setTimeout(loopDonations, POLL_DELAY_MS)
        window.addEventListener('kb-fake-donation', handleFakeDonation)
    })

    onUnmounted(() => {
        destroyed = true
        clearTimeout(campaignTimer)
        clearTimeout(donationTimer)
        window.removeEventListener('kb-fake-donation', handleFakeDonation)
    })

    return {
        name,
        totalAmount,
        targetAmount,
        progressPercent,
        currentNew,
        previousDonations
    }
}
